import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { api, ManagedUser, Partner, Plan, PlatformUser, session, SupportGrant, Tenant } from "./api";
import { can, isScopedSession } from "./can";
import { ListControls, usePagedList } from "./Pagination";
import { PageHeader } from "./ui";

export function CompaniesPage() {
  const currentUser = session.user();
  const scoped = isScopedSession(session.token(), currentUser);
  const isAdmin = currentUser?.platformRole === "platform_admin" && !scoped;
  // Revenda enxerga a carteira, mas não cria empresa nem plano; o Suporte Guardian Insight
  // pode criar empresa (tenant.manage) embora não veja conteúdo.
  const canManageTenants = can(currentUser, "tenant.manage") && !scoped;
  const isPortfolio = can(currentUser, "portfolio.read") && !canManageTenants;
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [platformUsers, setPlatformUsers] = useState<PlatformUser[]>([]);
  const [supportUsers, setSupportUsers] = useState<ManagedUser[]>([]);
  const [grants, setGrants] = useState<SupportGrant[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editingTenant, setEditingTenant] = useState<Tenant>();
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const pagedTenants = usePagedList(tenants);
  const refresh = async () => {
    setError("");
    try {
      setTenants(isPortfolio ? await api.myPortfolio() : await api.tenants());
      if (isAdmin) {
        const [users, support, activeGrants, partnerList] = await Promise.all([
          api.platformUsers(),
          api.supportUsers(),
          api.supportGrants(),
          api.partners(),
        ]);
        setPlatformUsers(users);
        setSupportUsers(support);
        setGrants(activeGrants);
        setPartners(partnerList);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao carregar empresas.");
    }
  };
  useEffect(() => {
    void refresh();
  }, []);
  const enter = async (tenant: Tenant) => {
    try {
      const result = await api.enterTenant(tenant.id);
      session.save(result.accessToken, result.user);
      location.hash = "#/";
      location.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Acesso não autorizado.");
    }
  };
  const assignPartner = async (tenant: Tenant, partnerId: string) => {
    setError("");
    try {
      await api.assignPartner(tenant.id, partnerId || undefined);
      setMessage("Revenda da empresa atualizada e auditada.");
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao vincular revenda.");
    }
  };
  const updateRole = async (
    user: PlatformUser,
    role?: PlatformUser["platformRole"],
  ) => {
    try {
      await api.updatePlatformRole(user.id, role);
      setMessage("Papel global atualizado e auditado.");
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao atualizar papel.");
    }
  };
  return (
    <>
      <PageHeader
        eyebrow="OPERAÇÃO MULTIEMPRESA"
        title={isPortfolio ? "Carteira de empresas" : "Empresas"}
        subtitle={
          isPortfolio
            ? "Empresas atendidas pela sua revenda. Cada acesso abre um contexto isolado e é auditado."
            : "Cada acesso abre um contexto isolado e gera registro de auditoria."
        }
        actions={canManageTenants && <button onClick={() => setShowCreate(true)}>+ Empresa</button>}
      />
      {error && <p className="error">{error}</p>}
      {message && <p className="success notice">{message}</p>}
      <section className="company-grid">
        {pagedTenants.pageItems.map((tenant) => (
          <article className="panel company-card" key={tenant.id}>
            <div>
              <small>{tenant.slug}</small>
              <h2>{tenant.name}</h2>
              {tenant.partnerName && <span className="tag-chip">{tenant.partnerName}</span>}
            </div>
            <dl>
              <div><dt>Usuários</dt><dd>{tenant.users}</dd></div>
              <div><dt>Equipamentos</dt><dd>{tenant.devices}</dd></div>
              {tenant.allocations.map((allocation) => <div key={allocation.planId}><dt>{allocation.planName}</dt><dd>{allocation.usedDevices}/{allocation.licensedDevices} vagas</dd></div>)}
            </dl>
            {isAdmin && (
              <label className="partner-assign">
                Revenda
                <select
                  value={tenant.partnerId ?? ""}
                  onChange={(event) => assignPartner(tenant, event.target.value)}
                >
                  <option value="">Sem revenda</option>
                  {partners.filter((partner) => partner.isActive).map((partner) => (
                    <option key={partner.id} value={partner.id}>{partner.name}</option>
                  ))}
                </select>
              </label>
            )}
            <button
              className={tenant.id === currentUser?.tenantId ? "secondary" : ""}
              onClick={() => enter(tenant)}
            >
              {tenant.id === currentUser?.tenantId ? "Atualizar contexto" : "Acessar empresa"}
            </button>
            {canManageTenants && <button className="secondary" onClick={() => setEditingTenant(tenant)}>Editar dados</button>}
          </article>
        ))}
        <ListControls page={pagedTenants.page} pages={pagedTenants.pages} size={pagedTenants.size} total={pagedTenants.total} onSize={pagedTenants.changeSize} onPrev={pagedTenants.prev} onNext={pagedTenants.next} />
      </section>
      {isAdmin && <PartnersPanel partners={partners} onChanged={refresh} />}
      {isAdmin && (
        <section className="panel platform-users">
          <div className="panel-title"><h2>Equipe interna Guardian Insight</h2><span>Papéis globais</span></div>
          <div className="table-wrap">
            <table><thead><tr><th>Usuário</th><th>Empresa base</th><th>Papel global</th><th>Ação</th></tr></thead>
              <tbody>{platformUsers.map((user) => (
                <tr key={user.id}><td><strong>{user.displayName}</strong><small>{user.email}</small></td><td>{user.tenantName}</td><td>{user.platformRole ?? "Sem acesso global"}</td><td><select value={user.platformRole ?? ""} onChange={(event) => updateRole(user, (event.target.value || undefined) as PlatformUser["platformRole"] | undefined)}><option value="">Sem acesso global</option><option value="platform_support">Suporte da plataforma</option><option value="platform_admin">Administrador da plataforma</option></select></td></tr>
              ))}</tbody></table>
          </div>
        </section>
      )}
      {isAdmin && supportUsers.length > 0 && (
        <SupportAccessPanel tenants={tenants} users={supportUsers} grants={grants} onChanged={refresh} />
      )}
      {showCreate && <CreateTenantModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); void refresh(); }} />}
      {editingTenant && <EditTenantModal tenant={editingTenant} onClose={() => setEditingTenant(undefined)} onSaved={() => { setEditingTenant(undefined); setMessage("Dados da empresa atualizados e auditados."); void refresh(); }} />}
    </>
  );
}

function PartnersPanel({ partners, onChanged }: { partners: Partner[]; onChanged: () => Promise<void> }) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const create = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.createPartner(name, slug);
      setName(""); setSlug("");
      await onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao criar revenda.");
    } finally {
      setBusy(false);
    }
  };
  const toggle = async (partner: Partner) => {
    setError("");
    try {
      await api.updatePartner(partner.id, partner.name, !partner.isActive);
      await onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao atualizar revenda.");
    }
  };
  return (
    <section className="panel partners-panel">
      <div className="panel-title"><h2>Revendas</h2><span>{partners.length} cadastradas</span></div>
      <form className="partner-form" onSubmit={create}>
        <label>Nome<input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} required /></label>
        <label>Identificador<input value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} pattern="[a-z0-9-]{2,40}" maxLength={40} required /></label>
        <button disabled={busy}>{busy ? "Criando…" : "Criar revenda"}</button>
      </form>
      {error && <p className="error">{error}</p>}
      <div className="table-wrap">
        <table>
          <thead><tr><th>Revenda</th><th>Empresas</th><th>Usuários</th><th>Estado</th><th>Ação</th></tr></thead>
          <tbody>
            {partners.map((partner) => (
              <tr key={partner.id}>
                <td><strong>{partner.name}</strong><small>{partner.slug}</small></td>
                <td>{partner.companies}</td>
                <td>{partner.users}</td>
                <td>{partner.isActive ? "Ativa" : "Inativa"}</td>
                <td><button className="small-button" onClick={() => toggle(partner)}>{partner.isActive ? "Desativar" : "Ativar"}</button></td>
              </tr>
            ))}
            {!partners.length && <tr><td colSpan={5}><span className="muted">Nenhuma revenda cadastrada.</span></td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function PlansPage() {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [error, setError] = useState("");
  const refresh = async () => {
    setError("");
    try {
      const [catalog, companies] = await Promise.all([api.platformPlans(), api.tenants()]);
      setPlans(catalog);
      setTenants(companies);
    } catch (e) { setError(e instanceof Error ? e.message : "Falha ao carregar planos e contratos."); }
  };
  useEffect(() => { void refresh(); }, []);
  return <>
    <PageHeader eyebrow="GESTÃO COMERCIAL" title="Planos e contratos" subtitle="Defina retenções e distribua vagas por empresa antes de ativar os equipamentos." />
    {error && <p className="error">{error}</p>}
    <PlansPanel plans={plans} tenants={tenants} onChanged={refresh} />
  </>;
}

function PlansPanel({ plans, tenants, onChanged }: { plans: Plan[]; tenants: Tenant[]; onChanged: () => Promise<void> }) {
  const [editing, setEditing] = useState<Plan>();
  const [newPlan, setNewPlan] = useState(false);
  const [error, setError] = useState("");
  const savePlan = async (plan: Plan) => {
    setError("");
    try {
      if (newPlan) await api.createPlan({ code: plan.code, name: plan.name, screenRetentionDays: plan.screenRetentionDays, operationalRetentionDays: plan.operationalRetentionDays, sortOrder: plan.sortOrder, isActive: plan.isActive, erpPrimaryId: plan.erpPrimaryId, erpSecondaryId: plan.erpSecondaryId });
      else await api.updatePlan(plan);
      setEditing(undefined); setNewPlan(false); await onChanged();
    } catch (e) { setError(e instanceof Error ? e.message : "Falha ao salvar plano."); }
  };
  const updateSeat = async (tenantId: string, planId: string, current: number) => {
    const raw = prompt("Quantidade de equipamentos contratada:", String(current));
    if (raw == null) return;
    const quantity = Number(raw);
    if (!Number.isInteger(quantity) || quantity < 0) return setError("Informe uma quantidade inteira válida.");
    try { await api.updateAllocation(tenantId, planId, quantity); await onChanged(); }
    catch (e) { setError(e instanceof Error ? e.message : "Falha ao atualizar vagas."); }
  };
  return <section className="panel plans-panel">
    <div className="panel-title"><div><h2>Planos e contratos</h2><span>Auditoria do sistema: 365 dias em todos os planos</span></div><button onClick={() => { setNewPlan(true); setEditing({ id: "", code: "", name: "", screenRetentionDays: 7, operationalRetentionDays: 15, sortOrder: (plans.length + 1) * 10, isActive: true }); }}>+ Plano</button></div>
    {error && <p className="error">{error}</p>}
    <div className="table-wrap"><table><thead><tr><th>Plano</th><th>Telas</th><th>Telemetria/eventos</th><th>Estado</th><th>Ação</th></tr></thead><tbody>{plans.map((plan) => <tr key={plan.id}><td><strong>{plan.name}</strong><small>{plan.code}</small></td><td>{plan.screenRetentionDays} dias</td><td>{plan.operationalRetentionDays} dias</td><td>{plan.isActive ? "Ativo" : "Inativo"}</td><td><button className="small-button" onClick={() => { setNewPlan(false); setEditing(plan); }}>Editar</button></td></tr>)}</tbody></table></div>
    <div className="allocation-grid">{tenants.map((tenant) => <article key={tenant.id}><strong>{tenant.name}</strong>{plans.filter((p) => p.isActive).map((plan) => { const allocation = tenant.allocations.find((a) => a.planId === plan.id); return <button className="secondary" key={plan.id} onClick={() => updateSeat(tenant.id, plan.id, allocation?.licensedDevices ?? 0)}>{plan.name}: {allocation?.usedDevices ?? 0}/{allocation?.licensedDevices ?? 0}</button>; })}</article>)}</div>
    {editing && <PlanModal plan={editing} onClose={() => { setEditing(undefined); setNewPlan(false); }} onSave={savePlan} />}
  </section>;
}

function PlanModal({ plan, onClose, onSave }: { plan: Plan; onClose: () => void; onSave: (plan: Plan) => Promise<void> }) {
  const [draft, setDraft] = useState(plan);
  return <div className="modal-backdrop" onMouseDown={onClose}><section className="modal" onMouseDown={(e) => e.stopPropagation()}><button className="close" onClick={onClose}>×</button><p className="eyebrow">CATÁLOGO COMERCIAL</p><h2>{plan.id ? "Editar plano" : "Novo plano"}</h2><form onSubmit={(e) => { e.preventDefault(); void onSave(draft); }}><label>Nome<input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} required /></label><label>Código interno<input pattern="[a-z0-9][a-z0-9-]*[a-z0-9]" value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value.toLowerCase() })} required /></label><label>Retenção de telas (dias)<input type="number" min="1" max="3650" value={draft.screenRetentionDays} onChange={(e) => setDraft({ ...draft, screenRetentionDays: Number(e.target.value) })} required /></label><label>Retenção de telemetria e eventos (dias)<input type="number" min="1" max="3650" value={draft.operationalRetentionDays} onChange={(e) => setDraft({ ...draft, operationalRetentionDays: Number(e.target.value) })} required /></label><label>ID ERP principal<input maxLength={80} value={draft.erpPrimaryId ?? ""} onChange={(e) => setDraft({ ...draft, erpPrimaryId: e.target.value })} placeholder="Conta Azul / Hermes" /></label><label>ID ERP secundário<input maxLength={80} value={draft.erpSecondaryId ?? ""} onChange={(e) => setDraft({ ...draft, erpSecondaryId: e.target.value })} /></label><label className="check"><input type="checkbox" checked={draft.isActive} onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })} /> Plano ativo</label><button>Salvar plano</button></form></section></div>;
}

type CommercialFields = { erpPrimaryId: string; erpSecondaryId: string; crmPrimaryId: string; crmSecondaryId: string; emailPrimary: string; emailSupport: string; emailBilling: string; phone: string; whatsappPrimary: string; whatsappSupport: string; cpf: string; cnpj: string };
const emptyCommercial: CommercialFields = { erpPrimaryId: "", erpSecondaryId: "", crmPrimaryId: "", crmSecondaryId: "", emailPrimary: "", emailSupport: "", emailBilling: "", phone: "", whatsappPrimary: "", whatsappSupport: "", cpf: "", cnpj: "" };

/** Campos comerciais compartilhados entre criar e editar empresa (ERP/CRM e contato). */
function TenantCommercialFields({ value, onChange }: { value: CommercialFields; onChange: (next: CommercialFields) => void }) {
  const set = (field: keyof CommercialFields) => (event: ChangeEvent<HTMLInputElement>) => onChange({ ...value, [field]: event.target.value });
  return (
    <details className="commercial-fields">
      <summary>Dados comerciais e contato</summary>
      <p className="muted">Usados na integração com ERP/CRM e no contato de cobrança. CPF ou CNPJ é obrigatório.</p>
      <div className="commercial-grid">
        <label>ID ERP principal<input maxLength={80} value={value.erpPrimaryId} onChange={set("erpPrimaryId")} /></label>
        <label>ID ERP secundário<input maxLength={80} value={value.erpSecondaryId} onChange={set("erpSecondaryId")} /></label>
        <label>ID CRM principal<input maxLength={80} value={value.crmPrimaryId} onChange={set("crmPrimaryId")} /></label>
        <label>ID CRM secundário<input maxLength={80} value={value.crmSecondaryId} onChange={set("crmSecondaryId")} /></label>
        <label>CPF<input inputMode="numeric" maxLength={14} placeholder="000.000.000-00" value={value.cpf} onChange={set("cpf")} /></label>
        <label>CNPJ<input inputMode="numeric" maxLength={18} placeholder="00.000.000/0000-00" value={value.cnpj} onChange={set("cnpj")} /></label>
        <label>E-mail principal<input type="email" value={value.emailPrimary} onChange={set("emailPrimary")} /></label>
        <label>E-mail de suporte<input type="email" value={value.emailSupport} onChange={set("emailSupport")} /></label>
        <label>E-mail de faturamento<input type="email" value={value.emailBilling} onChange={set("emailBilling")} /></label>
        <label>Telefone<input inputMode="tel" placeholder="+55 11 99999-9999" value={value.phone} onChange={set("phone")} /></label>
        <label>WhatsApp principal<input inputMode="tel" value={value.whatsappPrimary} onChange={set("whatsappPrimary")} /></label>
        <label>WhatsApp de suporte<input inputMode="tel" value={value.whatsappSupport} onChange={set("whatsappSupport")} /></label>
      </div>
    </details>
  );
}

function EditTenantModal({ tenant, onClose, onSaved }: { tenant: Tenant; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(tenant.name);
  const [commercial, setCommercial] = useState<CommercialFields>({
    erpPrimaryId: tenant.erpPrimaryId ?? "", erpSecondaryId: tenant.erpSecondaryId ?? "",
    crmPrimaryId: tenant.crmPrimaryId ?? "", crmSecondaryId: tenant.crmSecondaryId ?? "",
    emailPrimary: tenant.emailPrimary ?? "", emailSupport: tenant.emailSupport ?? "", emailBilling: tenant.emailBilling ?? "",
    phone: tenant.phone ?? "", whatsappPrimary: tenant.whatsappPrimary ?? "", whatsappSupport: tenant.whatsappSupport ?? "",
    cpf: tenant.cpf ?? "", cnpj: tenant.cnpj ?? "",
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.updateTenant(tenant.id, { name, ...commercial });
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao salvar os dados da empresa.");
    } finally {
      setBusy(false);
    }
  };
  return <div className="modal-backdrop" onMouseDown={onClose}><section className="modal" onMouseDown={(event) => event.stopPropagation()}><button className="close" onClick={onClose}>×</button><p className="eyebrow">DADOS DA EMPRESA</p><h2>{tenant.name}</h2><form onSubmit={submit}><label>Razão ou nome da empresa<input value={name} onChange={(event) => setName(event.target.value)} required /></label><TenantCommercialFields value={commercial} onChange={setCommercial} />{error && <p className="error">{error}</p>}<button disabled={busy}>{busy ? "Salvando…" : "Salvar dados"}</button></form></section></div>;
}

function CreateTenantModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [ownerPassword, setOwnerPassword] = useState("");
  const [commercial, setCommercial] = useState<CommercialFields>(emptyCommercial);
  const [error, setError] = useState("");
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    try { await api.createTenant({ name, slug, ownerEmail, ownerName, ownerPassword, ...commercial }); onCreated(); }
    catch (e) { setError(e instanceof Error ? e.message : "Falha ao criar empresa."); }
  };
  return <div className="modal-backdrop" onMouseDown={onClose}><section className="modal" onMouseDown={(event) => event.stopPropagation()}><button className="close" onClick={onClose}>×</button><p className="eyebrow">NOVA EMPRESA</p><h2>Criar ambiente isolado</h2><form onSubmit={submit}><label>Razão ou nome da empresa<input value={name} onChange={(event) => { setName(event.target.value); setSlug(event.target.value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")); }} required /></label><label>Identificador<input pattern="[a-z0-9][a-z0-9-]{1,78}[a-z0-9]" value={slug} onChange={(event) => setSlug(event.target.value.toLowerCase())} required /></label><label>Nome do proprietário<input value={ownerName} onChange={(event) => setOwnerName(event.target.value)} required /></label><label>E-mail do proprietário<input type="email" value={ownerEmail} onChange={(event) => setOwnerEmail(event.target.value)} required /></label><label>Senha inicial<input type="password" minLength={12} value={ownerPassword} onChange={(event) => setOwnerPassword(event.target.value)} autoComplete="new-password" required /></label><TenantCommercialFields value={commercial} onChange={setCommercial} />{error && <p className="error">{error}</p>}<button>Criar empresa</button></form></section></div>;
}

function SupportAccessPanel({ tenants, users, grants, onChanged }: { tenants: Tenant[]; users: ManagedUser[]; grants: SupportGrant[]; onChanged: () => Promise<void> }) {
  const [tenantId, setTenantId] = useState(tenants[0]?.id ?? "");
  const [supportUserId, setSupportUserId] = useState(users[0]?.id ?? "");
  const [justification, setJustification] = useState("");
  const create = async (event: FormEvent) => {
    event.preventDefault();
    await api.createSupportGrant(tenantId, supportUserId, justification, new Date(Date.now() + 2 * 3600000).toISOString());
    setJustification("");
    await onChanged();
  };
  return <section className="panel support-access"><div className="panel-title"><h2>Acessos temporários</h2><span>Expiração automática em 2 horas</span></div><form onSubmit={create}><label>Empresa<select value={tenantId} onChange={(event) => setTenantId(event.target.value)}>{tenants.map((tenant) => <option value={tenant.id} key={tenant.id}>{tenant.name}</option>)}</select></label><label>Analista<select value={supportUserId} onChange={(event) => setSupportUserId(event.target.value)}>{users.map((user) => <option value={user.id} key={user.id}>{user.displayName}</option>)}</select></label><label>Justificativa<input minLength={10} maxLength={500} value={justification} onChange={(event) => setJustification(event.target.value)} required /></label><button>Autorizar por 2 horas</button></form><div className="grant-list">{grants.map((grant) => <div key={grant.id}><span><strong>{grant.supportName}</strong>{grant.tenantName} · até {new Date(grant.expiresAt).toLocaleString("pt-BR")}</span><small>{grant.justification}</small>
                  <em className={grant.revokedAt ? "grant-status revoked" : new Date(grant.expiresAt) <= new Date() ? "grant-status expired" : "grant-status active"}>
                    {grant.revokedAt ? "Revogado" : new Date(grant.expiresAt) <= new Date() ? "Expirado" : "Ativo"}
                  </em>{!grant.revokedAt && new Date(grant.expiresAt) > new Date() && <button className="small-button danger" onClick={async () => { await api.revokeSupportGrant(grant.id); await onChanged(); }}>Revogar</button>}</div>)}</div></section>;
}

